import Badge from '@amzn/awsui-components-core/badge';
import Icon from '@amzn/awsui-components-core/icon';
import type { ProxySearchResultActions, ProxySearchResultBadges, SearchResult, SearchResultSection } from '@amzn/das-finsuite-shared-ui';
import { GlobalSearchBar as SharedGlobalSearchBar } from '@amzn/das-finsuite-shared-ui';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faExternalLinkAlt, faStar as solidStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateOrUpdateApplicationFavoritesMutation } from 'src/API';
import { fetchFinanceCalendarEvents } from 'src/api/app-sync-services';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import { useEmbeddedSearch } from 'src/hooks/useEmbeddedSearch';
import { logger } from 'src/logger';
import DFP_LOGO from '../../../images/finsuite-icon.svg';
import { useAppContext } from '../../context/AppContextProvider';
import { useAuth } from '../../context/AuthContextProvider';
import { UserActionsEventTypes } from '../../das-finsuite/das-finsuite-constants';
import { FinanceCalendarEventEntity } from '../../das-finsuite/widgets/finance-calendar-widget/FinanceCalendarData';
import { useNavigationItems } from '../../side-navigation-v2/useNavigationItems';
import { GroupedSearchResults, MAX_RESULTS_PER_CATEGORY, performSearch, QuickLinkItem, SearchResultItem } from './GlobalSearchUtils';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import { AppLogo } from './TopNavLogo';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const buildQuickLinks = (navItems: ReturnType<typeof useNavigationItems>): QuickLinkItem[] => {
  const links: QuickLinkItem[] = [];
  let currentSection = '';

  navItems.forEach((item) => {
    if (item.type === 'section-header') {
      currentSection = item.text;
    } else if (item.type === 'link' && item.href) {
      links.push({ id: `nav-${item.href}`, text: item.text, href: item.href, external: item.external, section: currentSection || 'Navigation' });
    }
  });

  PROFILE_DROPDOWN_ITEMS.forEach((group) => {
    if ('items' in group && group.items) {
      group.items.forEach((item) => {
        if ('href' in item && item.href && item.text) {
          links.push({ id: `profile-${item.id}`, text: item.text, href: item.href, external: true, section: 'Quick Links' });
        }
      });
    } else if ('href' in group && group.href && group.text) {
      links.push({ id: `profile-${group.id}`, text: group.text, href: group.href, external: true, section: 'Quick Links' });
    }
  });

  return links;
};

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '');

/** Convert FinSuite SearchResultItem → SharedUI SearchResult */
const toSearchResult = (item: SearchResultItem): SearchResult => ({
  id: item.id,
  type: item.category,
  label: item.title,
  description: item.description ? stripHtml(item.description) : undefined,
  meta: item.meta,
  path: item.href,
  externalUrl: item.external ? item.href : undefined,
  // Pass through FinSuite-specific data
  isFavorite: item.isFavorite,
  embeddingEnabled: item.embeddingEnabled,
  glAccount: item.glAccount,
  isFlagship: item.isFlagship,
  logoConfig: item.logoConfig,
  toolUI: item.toolUI
});

/** Build sections from grouped results */
const buildSections = (results: GroupedSearchResults, filteredProducts: SearchResultItem[]): SearchResultSection[] => {
  const sections: SearchResultSection[] = [];
  if (filteredProducts.length > 0) {
    sections.push({ title: 'Products', results: filteredProducts.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  if (results.pages.length > 0) {
    sections.push({ title: 'Links', results: results.pages.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  if (results.announcements.length > 0) {
    sections.push({ title: 'Product Launches', results: results.announcements.slice(0, MAX_RESULTS_PER_CATEGORY).map(toSearchResult) });
  }
  return sections;
};

// ─── Filter tag types ──────────────────────────────────────────────────────────

interface ProductFilterTag {
  label: string;
  count: number;
}

const buildProductFilterTags = (products: SearchResultItem[]): ProductFilterTag[] => {
  const tags: ProductFilterTag[] = [{ label: 'All', count: products.length }];
  const flagshipCount = products.filter((p) => p.isFlagship).length;
  const favoritesCount = products.filter((p) => p.isFavorite).length;
  if (flagshipCount > 0) tags.push({ label: 'Flagship', count: flagshipCount });
  if (favoritesCount > 0) tags.push({ label: 'Favorites', count: favoritesCount });

  const counts: Record<string, number> = {};
  products.forEach((p) => {
    counts[p.glAccount || 'Other'] = (counts[p.glAccount || 'Other'] || 0) + 1;
  });
  Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([label, count]) => tags.push({ label, count }));
  return tags;
};

// ─── Component ─────────────────────────────────────────────────────────────────

interface GlobalSearchBarProps {
  isEmbedActive?: boolean;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ isEmbedActive = false }) => {
  const navigate = useNavigate();
  const { Alias: userAlias } = useAuth();
  const { parsedApplications, favoriteApplications, setFavoriteApplications, announcementsFlatEntity, homePageContent } = useAppContext();
  const navItems = useNavigationItems();
  const embeddedSearch = useEmbeddedSearch(isEmbedActive);

  const [query, setQuery] = useState('');
  const [selectedGlAccount, setSelectedGlAccount] = useState('All');

  const activeParsedApps = useMemo(() => parsedApplications.filter((app) => app.isActive), [parsedApplications]);
  const quickLinks = useMemo(() => buildQuickLinks(navItems), [navItems]);
  const welcomeContent = useMemo(() => homePageContent?.welcomeContentDetails || [], [homePageContent]);

  const [financeCalendarEvents, setFinanceCalendarEvents] = useState<FinanceCalendarEventEntity[]>([]);
  useEffect(() => {
    fetchFinanceCalendarEvents()
      .then(setFinanceCalendarEvents)
      .catch(() => setFinanceCalendarEvents([]));
  }, []);

  const results: GroupedSearchResults = useMemo(
    () => performSearch(query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents),
    [query, activeParsedApps, announcementsFlatEntity, favoriteApplications, quickLinks, welcomeContent, financeCalendarEvents]
  );

  const glAccountTags = useMemo(() => buildProductFilterTags(results.products), [results.products]);

  const filteredProducts = useMemo(() => {
    if (selectedGlAccount === 'All') return results.products;
    if (selectedGlAccount === 'Flagship') return results.products.filter((p) => p.isFlagship);
    if (selectedGlAccount === 'Favorites') return results.products.filter((p) => p.isFavorite);
    return results.products.filter((p) => (p.glAccount || 'Other') === selectedGlAccount);
  }, [results.products, selectedGlAccount]);

  // Special filter values (All, Flagship, Favorites) are always valid selections
  // even if they temporarily disappear from glAccountTags during re-renders.
  // Only reset dynamic GL account tags (e.g. "FP&A", "Treasury") when they no longer exist.
  const SPECIAL_FILTERS = ['All', 'Flagship', 'Favorites'];
  useEffect(() => {
    const isSpecialFilter = SPECIAL_FILTERS.includes(selectedGlAccount);
    const currentTagExists = glAccountTags.some((t) => t.label === selectedGlAccount);
    if (!isSpecialFilter && !currentTagExists) {
      setSelectedGlAccount(glAccountTags[0]?.label || 'All');
    }
  }, [glAccountTags, selectedGlAccount]);

  // Reset to "All" tab whenever the search input regains focus (i.e. panel reopens)
  const finsuiteSearchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = finsuiteSearchRef.current;
    if (!wrapper) return;
    const input = wrapper.querySelector('input');
    if (!input) return;
    const handleFocus = () => setSelectedGlAccount('All');
    input.addEventListener('focus', handleFocus);
    return () => input.removeEventListener('focus', handleFocus);
  }, []);

  const sections = useMemo(() => buildSections(results, filteredProducts), [results, filteredProducts]);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (result.type === 'announcements') {
        navigate('/announcements', { state: { filterByTitle: result.label } });
        return;
      }
      if (result.id.startsWith('calendar-')) {
        navigate('/', { state: { highlightCalendarEvent: { scenario: result.meta, event: result.label } } });
        return;
      }
      if (result.type === 'products') {
        if (result.embeddingEnabled && result.path) {
          navigate(result.path);
        } else if (result.path) {
          window.open(result.path, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      if (result.externalUrl) {
        window.open(result.externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      if (result.path?.startsWith('/')) {
        navigate(result.path);
      } else if (result.path) {
        window.open(result.path, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate]
  );

  const toggleFavorite = useCallback(
    async (e: React.MouseEvent, result: SearchResult) => {
      e.stopPropagation();
      const isCurrentlyFavorite = favoriteApplications.includes(result.id);
      const updatedFavorites = isCurrentlyFavorite ? favoriteApplications.filter((id) => id !== result.id) : [...favoriteApplications, result.id];

      logger.info(`DaS FinSuite -> User Action: ${isCurrentlyFavorite ? 'Unfavorite' : 'Favorite'} App from Search`, {
        event_type: isCurrentlyFavorite ? UserActionsEventTypes.TOP_NAV_UNFAVORITE_APP : UserActionsEventTypes.TOP_NAV_FAVORITE_APP,
        value: result.label
      });
      setFavoriteApplications(updatedFavorites);

      try {
        await fetchGraphQLRequest<CreateOrUpdateApplicationFavoritesMutation>({
          query: mutations.createOrUpdateApplicationFavorites,
          variables: { input: { userAlias, applicationId: updatedFavorites } },
          requestType: appSyncOperationName.MUTATION,
          requestName: 'createOrUpdateApplicationFavorites'
        });
      } catch (error: any) {
        logger.error('Error updating favorites from search', error);
        setFavoriteApplications(favoriteApplications);
      }
    },
    [favoriteApplications, setFavoriteApplications, userAlias]
  );

  // ─── Render Slots ──────────────────────────────────────────────────────────

  const renderResultActions = useCallback(
    (result: SearchResult) => {
      if (result.type === 'products') {
        const logoConfig = result.logoConfig as SearchResultItem['logoConfig'];
        const renderIcon = () => {
          if (result.embeddingEnabled) {
            if (logoConfig && logoConfig.logoType === 'app-specific') {
              return (
                <span className="global-search__embedded-icon" aria-label="Opens within FinSuite">
                  <AppLogo logoConfig={logoConfig} />
                </span>
              );
            }
            return (
              <span className="global-search__embedded-icon" aria-label="Opens within FinSuite">
                <img src={DFP_LOGO} alt="" />
              </span>
            );
          }
          return (
            <span className="global-search__external-icon" aria-label="Opens in new tab">
              <Icon name="external" />
            </span>
          );
        };
        return (
          <div className="global-search__result-actions">
            <button
              className="global-search__favorite-btn"
              onClick={(e) => toggleFavorite(e, result)}
              aria-label={result.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <FontAwesomeIcon
                icon={result.isFavorite ? solidStar : regularStar}
                className={result.isFavorite ? 'global-search__star--active' : ''}
              />
            </button>
            {renderIcon()}
          </div>
        );
      }
      if (result.type === 'announcements' || result.externalUrl) {
        return (
          <span className="global-search__external-icon" aria-label="Opens in new tab">
            <Icon name="external" />
          </span>
        );
      }
      return null;
    },
    [toggleFavorite]
  );

  const renderFilterTags = useCallback(
    (sectionTitle: string) => {
      if (sectionTitle !== 'Products' || glAccountTags.length === 0) return null;
      return (
        <div className="global-search__tags">
          {glAccountTags.map((tag) => (
            <span
              key={tag.label}
              className="global-search__tag"
              onClick={() => setSelectedGlAccount(tag.label)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedGlAccount(tag.label);
                }
              }}
            >
              <Badge
                color={selectedGlAccount === tag.label ? 'blue' : 'grey'}
                style={
                  selectedGlAccount !== tag.label
                    ? { root: { background: 'transparent', borderColor: '#8d99a8', borderWidth: '1px', color: '#c9d1d9' } }
                    : undefined
                }
              >
                {tag.label} • {Math.min(tag.count, MAX_RESULTS_PER_CATEGORY)}
              </Badge>
            </span>
          ))}
        </div>
      );
    },
    [glAccountTags, selectedGlAccount]
  );

  const renderShowMore = useCallback(
    (sectionTitle: string) => {
      const hasMore =
        sectionTitle === 'Products'
          ? filteredProducts.length > MAX_RESULTS_PER_CATEGORY
          : sectionTitle === 'Product Launches'
          ? results.announcements.length > MAX_RESULTS_PER_CATEGORY
          : false;
      if (!hasMore) return null;
      return (
        <button
          className="global-search__show-more"
          onClick={() => {
            if (sectionTitle === 'Products') navigate('/all-apps');
            else navigate('/announcements');
          }}
        >
          Show more
        </button>
      );
    },
    [filteredProducts, results.announcements, navigate]
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  // Proxy mode: delegate to embedded app's search
  const proxyCapabilities = embeddedSearch.capabilities;
  const [proxyCategory, setProxyCategory] = useState<string>('all');
  const proxyQueryRef = useRef('');

  const proxyResultBadge = useCallback((result: SearchResult) => {
    const badges = result.badges as ProxySearchResultBadges | undefined;
    if (!badges?.typeBadge) return null;
    return <span className={`global-search__result-badge global-search__result-badge--${badges.typeBadge.variant}`}>{badges.typeBadge.label}</span>;
  }, []);

  const proxyResultActions = useCallback(
    (result: SearchResult) => {
      const actions = result.actions as ProxySearchResultActions | undefined;
      const badges = result.badges as ProxySearchResultBadges | undefined;
      return (
        <>
          {badges?.statusBadge && <Badge color="blue">{badges.statusBadge.label}</Badge>}
          {badges?.confidential && <Badge color="grey">Confidential</Badge>}
          {actions?.externalLink && (
            <span
              className="global-search__external-link-btn"
              onClick={(e) => {
                e.stopPropagation();
                window.open(actions?.externalLink?.url, '_blank', 'noopener,noreferrer');
              }}
              role="link"
              aria-label="Open external link"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </span>
          )}
          {actions?.favorite && (
            <span
              className={`global-search__favorite-btn ${actions.favorite.isFavorited ? 'global-search__favorite-btn--active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                embeddedSearch.sendAction(result.id, 'toggle-favorite');
              }}
              role="button"
              aria-label={actions.favorite.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <FontAwesomeIcon icon={actions.favorite.isFavorited ? solidStar : regularStar} />
            </span>
          )}
        </>
      );
    },
    [embeddedSearch]
  );

  if (proxyCapabilities) {
    return (
      <SharedGlobalSearchBar
        variant={proxyCapabilities.variant}
        placeholder={proxyCapabilities.placeholder}
        sections={embeddedSearch.sections}
        onQueryChange={(q) => {
          proxyQueryRef.current = q;
          setProxyCategory('all');
          embeddedSearch.sendQuery(q);
        }}
        onResultClick={(result) => embeddedSearch.sendResultClick({ path: result.path, externalUrl: result.externalUrl })}
        minQueryLength={2}
        categories={proxyCapabilities.categories}
        activeCategory={proxyCategory}
        onCategoryChange={(cat) => {
          setProxyCategory(cat);
          embeddedSearch.sendQuery(proxyQueryRef.current, cat);
        }}
        renderResultBadge={proxyResultBadge}
        renderResultActions={proxyResultActions}
        highlightQuery
      />
    );
  }

  return (
    <div ref={finsuiteSearchRef} style={{ width: '100%' }}>
      <SharedGlobalSearchBar
        variant="finsuite"
        placeholder="Search FinSuite  ·  apps, product launches, links..."
        sections={sections}
        onQueryChange={setQuery}
        onResultClick={handleResultClick}
        highlightQuery
        renderResultActions={renderResultActions}
        renderFilterTags={renderFilterTags}
        // renderShowMore={renderShowMore} //TODO: enable for next release
      />
    </div>
  );
};
